<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { Timeline } from 'vue-timeline-chart'
import 'vue-timeline-chart/style.css'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatTime } from '@/utils/datetime'
import { ChevronDown, ChevronUp } from 'lucide-vue-next'
import { DatePicker } from 'v-calendar'
import 'v-calendar/style.css'
import { useScreens } from 'vue-screen-utils'
import { useTimelineData } from './navigator/useTimelineData'
import { useTimelineViewport } from './navigator/useTimelineViewport'
import TimelineLineChart from './navigator/TimelineLineChart.vue'
import TimelineLegend from './navigator/TimelineLegend.vue'

const props = defineProps({
  dimensions: {
    type: Array,
    default: () => [],
  },
})

const store = useStore()
const settings = computed(() => store.getters['user/getUserSettings'])
const emit = defineEmits(['update:range'])

const topTimelineRef = ref(null)
const isCollapsed = ref(localStorage.getItem('timeline_collapsed') === 'true')

const {
  visibleDimensions,
  allDates,
  hasTimeline,
  singleGroup,
  allDatasets,
  datasetVisibility,
  toggleDatasetVisibility,
} = useTimelineData(props)

const {
  timeRangeOptions,
  selectedTimeRangeOption,
  filteredTimeRange,
  viewportMin,
  viewportMax,
} = useTimelineViewport(allDates)

// --- Viewport state ---
const currentViewportStart = ref(0)
const currentViewportEnd = ref(0)
const timelineKey = ref(0)

// --- DatePicker state ---
const isDatePickerOpen = ref(false)
const datePickerRef = ref(null)
const today = new Date(new Date().setHours(23, 59, 59, 999))
const disabledDates = ref([{ start: today }])

const { mapCurrent } = useScreens({
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
})
const datePickerColumns = mapCurrent({ lg: 2 }, 1)
const datePickerExpanded = mapCurrent({ lg: false }, true)

// Override for viewportMin/Max when datepicker selects outside data range
const datePickerOverrideMin = ref(null)
const datePickerOverrideMax = ref(null)

const effectiveViewportMin = computed(() => {
  if (datePickerOverrideMin.value !== null) {
    return Math.min(viewportMin.value, datePickerOverrideMin.value)
  }
  return viewportMin.value
})

const effectiveViewportMax = computed(() => {
  if (datePickerOverrideMax.value !== null) {
    return Math.max(viewportMax.value, datePickerOverrideMax.value)
  }
  return viewportMax.value
})

let isDatePickerUpdating = false

const datePickerRange = computed({
  get: () => ({
    start: new Date(currentViewportStart.value || filteredTimeRange.value[0]),
    end: new Date(currentViewportEnd.value || filteredTimeRange.value[1]),
  }),
  set: (value) => {
    if (value?.start && value?.end) {
      const start = value.start.getTime()
      const end = value.end.getTime()

      // Expand viewport bounds if datepicker range exceeds data range
      if (start < viewportMin.value) datePickerOverrideMin.value = start - 3600000
      if (end > viewportMax.value) datePickerOverrideMax.value = end + 3600000

      isDatePickerUpdating = true
      currentViewportStart.value = start
      currentViewportEnd.value = end
      emitRangeUpdate()
      timelineKey.value++
      nextTick(() => {
        nextTick(() => {
          isDatePickerUpdating = false
        })
      })
    }
  },
})

function setDatePickerRange(rangeString) {
  let start, end
  switch (rangeString) {
    case '24':
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
      end = today
      break
    case 'week':
      start = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
      end = today
      break
    case 'month':
      start = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate())
      end = today
      break
    case 'year':
      start = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())
      end = today
      break
    default:
      return
  }
  datePickerRange.value = { start, end }
  isDatePickerOpen.value = false
}

const selectionRange = computed(() => [
  new Date(currentViewportStart.value || filteredTimeRange.value[0]),
  new Date(currentViewportEnd.value || filteredTimeRange.value[1]),
])

// --- Debounced store update ---
let debounceTimer = null
const DEBOUNCE_MS = 500

function emitRangeUpdate() {
  const startDate = new Date(currentViewportStart.value || filteredTimeRange.value[0])
  startDate.setSeconds(0)
  startDate.setMilliseconds(0)

  const endDate = new Date(currentViewportEnd.value || filteredTimeRange.value[1])
  endDate.setSeconds(59)
  endDate.setMilliseconds(999)

  const rangeArray = [startDate.toISOString(), endDate.toISOString()]
  emit('update:range', rangeArray)
  store.commit('layers/SET_SELECTED_RANGE', rangeArray)
}

function debouncedEmitRangeUpdate() {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(emitRangeUpdate, DEBOUNCE_MS)
}

onBeforeUnmount(() => {
  if (debounceTimer) clearTimeout(debounceTimer)
  if (updatingFromDatesTimer) clearTimeout(updatingFromDatesTimer)
  stopOverviewDrag()
})

// --- Top timeline events ---
function onDetailChangeViewport({ start, end }) {
  if (isDatePickerUpdating || isUpdatingFromDates) return
  currentViewportStart.value = start
  currentViewportEnd.value = end
  debouncedEmitRangeUpdate()
}

// --- Overview selection item (background type) ---
const overviewSelectionItems = computed(() => {
  const start = currentViewportStart.value || filteredTimeRange.value[0]
  const end = currentViewportEnd.value || filteredTimeRange.value[1]
  return [
    {
      id: 'selection',
      type: 'background',
      start,
      end,
      className: 'overview-selection',
    },
  ]
})

// --- Overview drawer drag ---
const isDraggingOverview = ref(false)
const dragStartTime = ref(0)
const dragStartSelectionStart = ref(0)
const dragStartSelectionEnd = ref(0)
const overviewContainer = ref(null)

function getTimeFromPointer(event) {
  const el = overviewContainer.value
  if (!el) return null
  const rect = el.getBoundingClientRect()
  const x = (event.clientX || (event.touches && event.touches[0].clientX) || 0) - rect.left
  const fraction = x / rect.width
  return effectiveViewportMin.value + fraction * (effectiveViewportMax.value - effectiveViewportMin.value)
}

function onOverviewPointerDown(event) {
  const time = getTimeFromPointer(event)
  if (time === null) return

  const selStart = currentViewportStart.value || filteredTimeRange.value[0]
  const selEnd = currentViewportEnd.value || filteredTimeRange.value[1]

  if (time >= selStart && time <= selEnd) {
    // Start dragging the selection
    isDraggingOverview.value = true
    dragStartTime.value = time
    dragStartSelectionStart.value = selStart
    dragStartSelectionEnd.value = selEnd

    document.addEventListener('pointermove', onOverviewPointerMove)
    document.addEventListener('pointerup', onOverviewPointerUp)
    event.preventDefault()
  } else {
    // Click outside: center selection on clicked position
    const selDuration = selEnd - selStart
    let newStart = time - selDuration / 2
    let newEnd = time + selDuration / 2

    if (newStart < effectiveViewportMin.value) {
      newStart = effectiveViewportMin.value
      newEnd = newStart + selDuration
    }
    if (newEnd > effectiveViewportMax.value) {
      newEnd = effectiveViewportMax.value
      newStart = newEnd - selDuration
    }

    currentViewportStart.value = newStart
    currentViewportEnd.value = newEnd
    debouncedEmitRangeUpdate()
    nextTick(() => {
      if (topTimelineRef.value) {
        topTimelineRef.value.setViewport({ start: newStart, end: newEnd })
      }
    })
  }
}

function onOverviewPointerMove(event) {
  if (!isDraggingOverview.value) return

  const time = getTimeFromPointer(event)
  if (time === null) return

  const timeDelta = time - dragStartTime.value
  const selDuration = dragStartSelectionEnd.value - dragStartSelectionStart.value

  let newStart = dragStartSelectionStart.value + timeDelta
  let newEnd = dragStartSelectionEnd.value + timeDelta

  if (newStart < effectiveViewportMin.value) {
    newStart = effectiveViewportMin.value
    newEnd = newStart + selDuration
  }
  if (newEnd > effectiveViewportMax.value) {
    newEnd = effectiveViewportMax.value
    newStart = newEnd - selDuration
  }

  currentViewportStart.value = newStart
  currentViewportEnd.value = newEnd
}

function onOverviewPointerUp() {
  if (!isDraggingOverview.value) return
  isDraggingOverview.value = false
  stopOverviewDrag()
  debouncedEmitRangeUpdate()
  timelineKey.value++
}

function stopOverviewDrag() {
  document.removeEventListener('pointermove', onOverviewPointerMove)
  document.removeEventListener('pointerup', onOverviewPointerUp)
}

// --- Controls ---
function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value
  localStorage.setItem('timeline_collapsed', isCollapsed.value)
}

function resetTimeRange() {
  selectedTimeRangeOption.value = 'all'
}

// --- Init ---
onMounted(() => {
  const [start, end] = filteredTimeRange.value
  currentViewportStart.value = start
  currentViewportEnd.value = end
  emitRangeUpdate()
})

let isUpdatingFromDates = false
let updatingFromDatesTimer = null

watch(
  () => allDates.value,
  () => {
    if (isUpdatingFromDates || isDatePickerUpdating) return
    isUpdatingFromDates = true
    if (updatingFromDatesTimer) clearTimeout(updatingFromDatesTimer)
    datePickerOverrideMin.value = null
    datePickerOverrideMax.value = null
    const [start, end] = filteredTimeRange.value
    currentViewportStart.value = start
    currentViewportEnd.value = end
    emitRangeUpdate()
    timelineKey.value++
    updatingFromDatesTimer = setTimeout(() => {
      isUpdatingFromDates = false
    }, 1000)
  }
)

watch(selectedTimeRangeOption, () => {
  datePickerOverrideMin.value = null
  datePickerOverrideMax.value = null
  const [start, end] = filteredTimeRange.value
  currentViewportStart.value = start
  currentViewportEnd.value = end
  emitRangeUpdate()
  timelineKey.value++
})
</script>

<template>
  <div
    v-if="hasTimeline"
    class="select-none bg-background shadow-lg rounded-lg"
    :class="
      isCollapsed
        ? 'inline-block cursor-pointer hover:bg-muted hover:ring-1 hover:ring-primary/20 transition-all p-0.5'
        : 'w-full p-1'
    "
    @click="isCollapsed && toggleCollapse()"
    :title="isCollapsed ? 'Click to expand timeline' : ''"
  >
    <div :class="{ 'w-full': !isCollapsed }">
      <!-- Header -->
      <div
        :class="[
          isCollapsed
            ? 'inline-flex items-center px-1 py-1'
            : 'flex justify-between items-center mb-1',
        ]"
      >
        <div class="flex items-center space-x-1 pr-1">
          <Button
            size="sm"
            variant="secondary"
            class="shrink-0 p-1 h-auto"
            @click.stop="toggleCollapse"
            :title="isCollapsed ? 'Expand timeline' : 'Collapse timeline'"
          >
            <ChevronUp v-if="isCollapsed" class="h-4 w-4 text-primary" />
            <ChevronDown v-else class="h-4 w-4" />
            <span class="text-sm ml-2 pr-1" v-if="!isCollapsed">Hide</span>
          </Button>
        </div>

        <div v-if="!isCollapsed" class="flex items-center">
          <Popover :open="isDatePickerOpen" @update:open="isDatePickerOpen = $event">
            <PopoverTrigger as-child>
              <span class="text-muted-foreground text-sm cursor-pointer hover:text-foreground hover:underline transition-colors">
                {{ formatTime(selectionRange[0], settings.tz, 'D MMMM YYYY HH:mm') }} -
                {{ formatTime(selectionRange[1], settings.tz, 'D MMMM YYYY HH:mm') }}
              </span>
            </PopoverTrigger>
            <PopoverContent class="p-0 w-full" side="top" @click.stop>
              <div class="p-1">
                <DatePicker
                  class="font-sans"
                  ref="datePickerRef"
                  borderless
                  trim-weeks
                  v-model.range="datePickerRange"
                  :columns="datePickerColumns"
                  :step="1"
                  :initial-page-position="-1"
                  :expanded="datePickerExpanded"
                  :disabled-dates="disabledDates"
                />
              </div>
            </PopoverContent>
          </Popover>
          <Button v-if="selectedTimeRangeOption !== 'all'" class="ml-2" variant="secondary" size="sm" @click="resetTimeRange">
            Reset
          </Button>
          <div class="flex items-center ml-3">
            <select
              v-model="selectedTimeRangeOption"
              class="text-sm rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option
                v-for="option in timeRangeOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <div v-if="isCollapsed" class="text-muted-foreground truncate ml-1 mr-1">
          {{ formatTime(selectionRange[0], settings.tz, 'D MMM HH:mm') }} -
          {{ formatTime(selectionRange[1], settings.tz, 'D MMM HH:mm') }}
        </div>
      </div>

      <!-- Legend -->
      <TimelineLegend
        v-if="!isCollapsed && allDatasets.length > 1"
        class="pb-1"
        :datasets="allDatasets"
        :visibility="datasetVisibility"
        @toggle="toggleDatasetVisibility"
      />

      <div v-if="!isCollapsed" class="w-full">
        <!-- Top: Detail timeline (scrollable/zoomable) -->
        <div class="detail-timeline">
          <Timeline
            :key="'detail-' + timelineKey"
            ref="topTimelineRef"
            :groups="singleGroup"
            :items="[]"
            :viewportMin="effectiveViewportMin"
            :viewportMax="effectiveViewportMax"
            :initialViewportStart="Math.min(currentViewportStart || filteredTimeRange[0], (currentViewportEnd || filteredTimeRange[1]) - 1)"
            :initialViewportEnd="currentViewportEnd || filteredTimeRange[1]"
            @changeViewport="onDetailChangeViewport"
          >
            <template #items-chart="{ viewportStart, viewportEnd }">
              <TimelineLineChart
                :dimensions="visibleDimensions"
                :viewportStart="viewportStart"
                :viewportEnd="viewportEnd"
              />
            </template>
          </Timeline>
        </div>

        <!-- Bottom: Overview timeline (fixed, with draggable selection) -->
        <div ref="overviewContainer" class="overview-timeline">
          <Timeline
            :key="'overview-' + timelineKey"
            :groups="singleGroup"
            :items="overviewSelectionItems"
            :viewportMin="effectiveViewportMin"
            :viewportMax="effectiveViewportMax"
            :initialViewportStart="Math.min(effectiveViewportMin, effectiveViewportMax - 1)"
            :initialViewportEnd="effectiveViewportMax"
            :maxViewportDuration="Math.max(effectiveViewportMax - effectiveViewportMin, 1)"
            :minViewportDuration="Math.max(effectiveViewportMax - effectiveViewportMin, 1)"
          >
            <template #items-chart="{ viewportStart, viewportEnd }">
              <TimelineLineChart
                :dimensions="visibleDimensions"
                :viewportStart="viewportStart"
                :viewportEnd="viewportEnd"
                :compact="true"
              />
            </template>
          </Timeline>
          <!-- Transparent overlay to capture drag events above the library -->
          <div
            class="overview-drag-overlay"
            @pointerdown="onOverviewPointerDown"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.detail-timeline {
  --group-items-height: 6em;
}

.detail-timeline .vue-timeline-chart .group-labels,
.detail-timeline .vue-timeline-chart .group-label {
  display: none;
}

.overview-timeline {
  --group-items-height: 30px;
  --group-padding-top: 0px;
  --group-padding-bottom: 0px;
  --group-border-top: none;
  --timestamp-padding-block: 0px;
  --timestamp-line-height: 0px;
  --timestamps-background: transparent;
  position: relative;
  cursor: pointer;
  border-top: 1px solid hsl(var(--border));
}

.overview-drag-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  cursor: grab;
}

.overview-drag-overlay:active {
  cursor: grabbing;
}

.overview-timeline .timeline-wrapper .timestamps {
  height: 0 !important;
  min-height: 0 !important;
  overflow: hidden !important;
  visibility: hidden !important;
}

.overview-timeline .vue-timeline-chart .group-labels,
.overview-timeline .vue-timeline-chart .group-label {
  display: none;
}

.overview-selection {
  background: hsl(var(--primary) / 0.15);
  border-left: 2px solid hsl(var(--primary));
  border-right: 2px solid hsl(var(--primary));
  cursor: grab;
  z-index: 10;
}

.overview-selection:active {
  cursor: grabbing;
}

.vc-container {
  --vc-font-family: 'Inter', sans-serif;
}
</style>
